import React, { SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  PanoMediaControlsContainer,
  MediaTimeLine,
  MediaControl,
  MediaSpan,
  VolumeSlider,
} from "./PanoStyles";

import { ReactComponent as PauseIcon } from "../../images/pause-button.svg";
import { ReactComponent as PlayIcon } from "../../images/play-arrow.svg";
import { ReactComponent as MuteIcon } from "../../images/mute.svg";
import { ReactComponent as UnmuteIcon } from "../../images/unmute.svg";
import poiPause from "../../images/markers/poiPause.svg";
import poiPlay from "../../images/markers/poiPlay.svg";
import { ratioToPercentage, toTimeDisplay } from "../../utils/mediaUtils";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocalStorage, useSessionStorage } from "../../hooks/useStorage";
import { useInterfaceState } from "../../InterfaceContext";
import { Flex } from "../../shared/components/Flex";
import { useKrpano } from "./Krpano";
import { Krpano } from "../../libs/krpano";
import { makeKeyDownHandler } from "../../utils/a11y-utils";
import { Pano, PanoScenario } from "../../types";

interface PanoVideoControlsProps {
  pano: Pano;
}

const muteKey = "muteMedia";
const mediaVolumeKey = "mediaVolume";

const volumeStepSize = 0.05;

const checkHasAudio = (video: HTMLVideoElement) => {
  switch (true) {
    case "mozHasAudio" in video:
      return (video as any)["mozHasAudio"];
    case "webkitAudioDecodedByteCount" in video:
      return (video as any)["webkitAudioDecodedByteCount"] > 0;
    case "audioTracks" in video:
      return (video as any)["audioTracks"].length > 0;
  }

  // A known way to check audio was not found, assume the video has audio
  return true;
};

const patchDestroyVideo = (videoPlugin?: Krpano.Plugin | null) => {
  if (!videoPlugin?.videoDOM || videoPlugin.destroyPatched) return;

  videoPlugin.destroy = (
    (originalDestroy: (...args: any[]) => any) =>
    (...args: any[]) => {
      videoPlugin.videoDOM.src = null;
      videoPlugin.videoDOM.load();
      originalDestroy.call(videoPlugin, ...args);
    }
  )(videoPlugin.destroy);

  videoPlugin.destroyPatched = true;
};

export function PanoVideoControls({ pano }: PanoVideoControlsProps) {
  const showVideoControls = pano.showVideoControls;
  const krpano = useKrpano();
  const [currentTime, setCurrentTime] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [hasAudio, setHasAudio] = useState(true);
  const [mutedRaw, setMutedRaw] = useSessionStorage(muteKey);
  const [volumeRaw, setVolumeRaw] = useLocalStorage(mediaVolumeKey);
  const intl = useIntl();
  const { isInVr } = useInterfaceState();
  const volumeSliderRef = useRef<HTMLInputElement>(null);

  const vrPlayButton = krpano?.get("hotspot[toggle-play]");
  const vrEnabled = krpano?.get("webvr.isenabled") ?? false;
  const videoPlugin = krpano?.get("plugin[video]");

  const muted = useMemo(() => (mutedRaw ? JSON.parse(mutedRaw) : false), [mutedRaw]);
  const volume = useMemo(() => (volumeRaw ? JSON.parse(volumeRaw) : 0.5), [volumeRaw]);

  const setVolume = useCallback(
    (value: number) => setVolumeRaw(JSON.stringify(value)),
    [setVolumeRaw]
  );

  const setMuted = useCallback(
    (value: boolean) => setMutedRaw(JSON.stringify(value)),
    [setMutedRaw]
  );

  useEffect(() => patchDestroyVideo(videoPlugin), [videoPlugin]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setCurrentTime((c) => videoPlugin?.time ?? c);
      setTotalTime((c) => videoPlugin?.totaltime ?? c);
      setHasAudio((c) => (videoPlugin?.videoDOM ? checkHasAudio(videoPlugin.videoDOM) : c));
    }, 100);

    return () => window.clearInterval(interval);
  }, [videoPlugin]);

  useEffect(() => {
    if (videoPlugin) videoPlugin.volume = volume;
  }, [videoPlugin, volume]);

  useEffect(() => {
    if (videoPlugin) videoPlugin.muted = muted;
  }, [videoPlugin, muted]);

  const togglePlaying = useCallback(() => {
    videoPlugin?.togglepause();
  }, [videoPlugin]);

  const seek = useCallback(
    (event: SyntheticEvent<HTMLElement, MouseEvent>) => {
      const ratio = event.nativeEvent.offsetX / (event.target as HTMLElement).clientWidth;
      videoPlugin?.seek(ratio * videoPlugin.totaltime);
    },
    [videoPlugin]
  );

  const scrub = useCallback(
    (event: SyntheticEvent<HTMLElement, KeyboardEvent>) => {
      if (!videoPlugin) return;

      const delta =
        event.nativeEvent.ctrlKey || event.nativeEvent.metaKey
          ? 60
          : event.nativeEvent.shiftKey
          ? 10
          : 1;

      switch (event.nativeEvent.key) {
        case " ":
        case "Enter":
          event.preventDefault();
          togglePlaying();
          break;
        case "ArrowLeft":
          videoPlugin.seek(Math.max(0, videoPlugin.time - delta));
          break;
        case "ArrowRight":
          videoPlugin.seek(Math.min(videoPlugin.totaltime, videoPlugin.time + delta));
          break;
      }
    },
    [videoPlugin, togglePlaying]
  );

  const toggleMute = useCallback(() => {
    if (!videoPlugin) return;

    if (videoPlugin.volume === 0 && !videoPlugin.muted) {
      setVolume(0.5);
    } else {
      setMuted(!videoPlugin.muted);
    }
  }, [videoPlugin, setMuted, setVolume]);

  useEffect(() => {
    if (!krpano) return;

    if (vrPlayButton && videoPlugin) {
      vrPlayButton.ath = krpano.get("view.hlookat");
      vrPlayButton.atv = krpano.get("view.vlookat") + 20;
      vrPlayButton.visible = showVideoControls && vrEnabled;
      vrPlayButton.url = videoPlugin.ispaused ? poiPause : poiPlay;
    }
  }, [vrEnabled, vrPlayButton, showVideoControls, videoPlugin, krpano]);

  const updateVolume = useCallback(
    (value: number) => {
      if (!videoPlugin) return;

      if (videoPlugin.muted && value) {
        setMuted(false);
      }
      setVolume(value);
    },
    [videoPlugin, setMuted, setVolume]
  );

  const controlVolume = useCallback(
    (event: SyntheticEvent<HTMLElement, KeyboardEvent>) => {
      if (!videoPlugin) return;

      switch (event.nativeEvent.key) {
        case " ":
        case "Enter":
          event.preventDefault();
          toggleMute();
          break;
        case "ArrowLeft":
          if (!volumeSliderRef.current) return;
          volumeSliderRef.current.focus();
          updateVolume(Math.max(0, volumeSliderRef.current.valueAsNumber - volumeStepSize));
          break;
        case "ArrowRight":
          if (!volumeSliderRef.current) return;
          volumeSliderRef.current.focus();
          updateVolume(Math.min(1, volumeSliderRef.current.valueAsNumber + volumeStepSize));
          break;
      }
    },
    [videoPlugin, toggleMute, scrub]
  );

  const muteLabel = intl.formatMessage({
    id: "pano.media.unmute-aria-label",
    defaultMessage: "Turn off sound",
    description: "ARIA label for the mute button while the media is currently muted.",
  });
  const unmuteLabel = intl.formatMessage({
    id: "pano.media.mute-aria-label",
    defaultMessage: "Turn on sound",
    description: "ARIA label for the mute button while the media is not currently muted.",
  });
  const pauseLabel = intl.formatMessage({
    id: "pano.media.pause-aria-label",
    defaultMessage: "Pause",
    description: "ARIA label for the pause button.",
  });
  const playLabel = intl.formatMessage({
    id: "pano.media.play-aria-label",
    defaultMessage: "Play",
    description: "ARIA label for the play button.",
  });
  const noSoundLabel = intl.formatMessage({
    id: "pano.media.no-sound-label",
    defaultMessage: "Video has no sound",
    description: "ARIA label for the play button.",
  });

  if (isInVr || !videoPlugin) return null;

  return (
    <PanoMediaControlsContainer>
      <Flex column gap=".2em">
        <MediaTimeLine onClick={seek} tabIndex={0} onKeyDown={scrub}>
          <div style={{ width: `${ratioToPercentage(currentTime, totalTime)}%` }} />
        </MediaTimeLine>
        <Flex style={{ alignItems: "center", padding: "0 0.5rem" }}>
          <MediaControl
            onClick={togglePlaying}
            onKeyDown={makeKeyDownHandler(togglePlaying)}
            tabIndex={0}
          >
            {!videoPlugin.ispaused ? (
              <PauseIcon title={pauseLabel} aria-label={pauseLabel} />
            ) : (
              <PlayIcon title={playLabel} aria-label={playLabel} />
            )}
          </MediaControl>
          {hasAudio ? (
            <Flex>
              <MediaControl onClick={toggleMute} onKeyDown={controlVolume} tabIndex={0}>
                {videoPlugin.muted || !videoPlugin.volume ? (
                  <MuteIcon title={unmuteLabel} aria-label={unmuteLabel} />
                ) : (
                  <UnmuteIcon title={muteLabel} aria-label={muteLabel} />
                )}
              </MediaControl>
              <VolumeSlider
                ref={volumeSliderRef}
                type="range"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateVolume(e.target.valueAsNumber)
                }
                value={muted ? 0 : volume}
                step={0.05}
                max={1}
              />
            </Flex>
          ) : (
            <MediaControl onClick={() => {}}>
              <MuteIcon title={noSoundLabel} aria-label={noSoundLabel} fillOpacity={0.5} />
            </MediaControl>
          )}

          <MediaSpan aria-hidden>
            {toTimeDisplay(currentTime)} / {toTimeDisplay(totalTime)}
          </MediaSpan>
          <MediaSpan>
            <FormattedMessage
              id="pano.media-type.video"
              defaultMessage="Video"
              description="Label depicting the currently playing media is a panoramic video."
            />
          </MediaSpan>
        </Flex>
      </Flex>
    </PanoMediaControlsContainer>
  );
}
